import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAppStore } from '@/store';
import { Link } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Award, Bot, Home, Upload, Trash2, User } from 'lucide-react';
import { useProfile } from '@/hooks/useProfile';
import { useAuth } from '@/contexts/AuthContext';
import UserBadge from '@/components/users/UserBadge';
import { useSupabaseStore } from '@/store/SupabaseStore';
import { Profile as ProfileType } from '@/types/supabase';
import { fetchOllamaModels } from '@/lib/ollama';
import { AIModel, ModelProvider, UserProfile } from '@/types';
import ModelSelector from '@/components/chat/ModelSelector';
import LoadingScreen from '@/components/ui/loading-screen';
import PasswordSettings from '@/components/settings/PasswordSettings';
import LandingShell from '@/components/landing/LandingShell';
import { cn } from '@/lib/utils';

const FOUNDERS_END_DATE = new Date('2025-09-01T00:00:00Z');

export function ProfilePanel({ embedded = false }: { embedded?: boolean }) {
  const { user, setUser } = useAppStore();
  const { profile, updateProfile, uploadAvatar, deleteAvatar } = useProfile();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user: authUser } = useAuth();
  const { models, fetchModels } = useSupabaseStore();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [defaultModel, setDefaultModel] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [ollamaModels, setOllamaModels] = useState<Array<{ name: string; source: 'local' | 'remote' }>>([]);
  const [isLoadingOllama, setIsLoadingOllama] = useState(false);

  useEffect(() => {
    fetchModels();
    setIsLoadingOllama(true);
    fetchOllamaModels()
      .then(setOllamaModels)
      .catch(() => setOllamaModels([]))
      .finally(() => setIsLoadingOllama(false));
  }, [fetchModels]);

  const availableModels = useMemo(() => {
    const ollamaVirtualModels: AIModel[] = ollamaModels.map(({ name, source }) => ({
      id: `ollama-${source}-${name.replace(/[:]/g, '-').replace(/\s/g, '')}`,
      name,
      provider: 'ollama' as ModelProvider,
      description: `Ollama (${source === 'local' ? 'Local' : 'Remote'}): ${name}`,
      capabilities: ['text', 'code'],
      tier: 'free',
      apiName: name,
      is_active: true,
      source,
      created_at: new Date().toISOString(),
      context_length: null,
      logo_url: null,
    }));

    const combined = [...models, ...ollamaVirtualModels];
    const uniqueModels: AIModel[] = [];
    const seenApiNames = new Set<string>();

    combined.forEach((model) => {
      if (!seenApiNames.has(model.apiName)) {
        uniqueModels.push(model);
        seenApiNames.add(model.apiName);
      }
    });

    return uniqueModels.sort((a, b) => a.name.localeCompare(b.name));
  }, [models, ollamaModels]);

  useEffect(() => {
    if (availableModels.length === 0) return;

    const userProfile = profile as UserProfile | null;
    const selectedModel = userProfile?.defaultModelApiName
      ? availableModels.find((model) => model.apiName === userProfile.defaultModelApiName)
      : availableModels[0];

    if (selectedModel) {
      setDefaultModel(selectedModel.id);
    }
  }, [profile, availableModels]);

  useEffect(() => {
    if (!profile && authUser) {
      setName(authUser.email?.split('@')[0] || '');
      setEmail(authUser.email || '');
      return;
    }

    if (profile) {
      setName(profile.name || '');
      setEmail(authUser?.email || profile.email || '');
    }
  }, [profile, authUser]);

  const handleSaveAll = async () => {
    if (!user || !profile) {
      toast({
        title: 'Error',
        description: 'User profile not fully loaded. Please wait a moment and try again.',
        variant: 'destructive',
      });
      return;
    }

    const selectedModel = availableModels.find((model) => model.id === defaultModel);

    try {
      const updates: Partial<ProfileType> = {
        full_name: name,
        default_model: selectedModel?.apiName,
      };

      const { success } = await updateProfile(updates);

      if (success) {
        const updatedUser = {
          ...user,
          name,
          email,
          defaultModelApiName: selectedModel?.apiName,
        };
        setUser(updatedUser as UserProfile);
        toast({
          title: 'All Changes Saved',
          description: 'Your profile and settings have been updated successfully.',
        });
      }
    } catch (error) {
      toast({
        title: 'Error Saving Changes',
        description: `An unexpected error occurred: ${(error as Error).message}`,
        variant: 'destructive',
      });
    }
  };

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Maximum file size is 2MB',
        variant: 'destructive',
      });
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload an image file',
        variant: 'destructive',
      });
      return;
    }

    try {
      setUploading(true);
      const result = await uploadAvatar(file);
      if (result.success && result.publicUrl && user) {
        setUser({ ...user, avatar: result.publicUrl } as UserProfile);
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteAvatar = async () => {
    const result = await deleteAvatar();
    if (result.success && user) {
      setUser({ ...user, avatar: undefined } as UserProfile);
    }
  };

  const avatarUrl = profile?.avatar || user?.avatar;

  const createdAt = (profile as any)?.created_at || (user as any)?.created_at;
  let isFounder = false;
  if (createdAt) {
    try {
      isFounder = new Date(createdAt) < FOUNDERS_END_DATE;
    } catch {
      isFounder = false;
    }
  }

  const userBadges = isFounder ? [{ type: 'founder' as const }] : [];
  const selectedModelName = availableModels.find((model) => model.id === defaultModel)?.name;

  if (!profile) {
    return <LoadingScreen />;
  }

  return (
    <div
      className={cn(
        embedded
          ? 'h-full overflow-y-auto overflow-x-hidden bg-[linear-gradient(180deg,hsl(var(--muted)/0.18),transparent_180px)]'
          : 'container mx-auto space-y-8 py-8'
      )}
    >
      <div className={cn(embedded ? 'space-y-5 p-4 md:p-5' : 'space-y-8')}>
        {embedded ? (
          <div className="rounded-xl border border-border/70 bg-card/90 px-4 py-3 shadow-sm backdrop-blur-sm">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h1 className="text-2xl font-semibold tracking-tight">Profile</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  Manage your account, avatar, badges, and default AI model.
                </p>
              </div>
              <div className="rounded-lg border border-border/70 bg-muted/40 px-3 py-2 text-right shadow-sm">
                <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Default model</p>
                <p className="mt-1 max-w-48 truncate text-sm font-medium">{selectedModelName || 'Not set'}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold">User Profile</h1>
              <p className="mt-1 text-sm text-muted-foreground">Manage your profile, model defaults, badges, and security.</p>
            </div>
            <Link to="/">
              <Button variant="outline" className="gap-2">
                <Home size={18} />
                Back to Home
              </Button>
            </Link>
          </div>
        )}

        <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1.25fr)_minmax(280px,0.8fr)]">
          <Card className={cn(embedded && 'border-border/70 bg-card/95 shadow-sm')}>
            <CardHeader>
              <div className="flex items-center gap-2">
                <User size={20} />
                <CardTitle>Profile Information</CardTitle>
              </div>
              <CardDescription>Update your personal information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="mb-6 flex flex-col items-center space-y-4">
                <div className="group relative flex flex-col items-center">
                  <Avatar
                    className="h-24 w-24 cursor-pointer border-2 border-primary/10 transition-all group-hover:border-primary/50"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {avatarUrl ? (
                      <AvatarImage src={avatarUrl} alt={name || 'User avatar'} />
                    ) : (
                      <AvatarFallback>
                        <Bot size={40} />
                      </AvatarFallback>
                    )}
                  </Avatar>
                  <div className="absolute bottom-0 right-0 cursor-pointer rounded-full bg-primary p-2 text-primary-foreground shadow-sm">
                    <Upload size={16} />
                  </div>
                </div>
                <Input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarUpload} className="hidden" />
                {avatarUrl && (
                  <Button variant="outline" size="sm" onClick={handleDeleteAvatar} disabled={uploading}>
                    <Trash2 size={16} className="mr-2" />
                    Remove Avatar
                  </Button>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input id="name" value={name} onChange={(event) => setName(event.target.value)} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" value={email} disabled />
              </div>

              <div className="space-y-2">
                <Label>Default Model</Label>
                <ModelSelector
                  models={availableModels}
                  selectedModelId={defaultModel}
                  onModelChange={setDefaultModel}
                />
                {isLoadingOllama && <p className="text-xs text-muted-foreground">Loading local models…</p>}
              </div>

              <div className={cn('pt-2', embedded && 'sticky bottom-0 -mx-6 border-t border-border/70 bg-card/95 px-6 pb-0 pt-4 backdrop-blur-sm')}>
                <Button className="w-full" onClick={handleSaveAll} disabled={uploading}>
                  Save Changes
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-5">
            <Card className={cn(embedded && 'border-border/70 bg-card/95 shadow-sm')}>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Award size={20} />
                  <CardTitle>Badges</CardTitle>
                </div>
                <CardDescription>Your achievements and account milestones will appear here.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {userBadges.length > 0 ? (
                  <div className="flex flex-wrap gap-3">
                    {userBadges.map((badge) => (
                      <UserBadge key={badge.type} type={badge.type} />
                    ))}
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed p-4 text-sm text-muted-foreground">
                    No badges yet.
                  </div>
                )}
              </CardContent>
            </Card>

            {embedded && (
              <Card className="border-border/70 bg-card/95 shadow-sm">
                <CardHeader>
                  <CardTitle className="text-base">Account Summary</CardTitle>
                  <CardDescription>Quick details for this MapleOS session.</CardDescription>
                </CardHeader>
                <CardContent className="grid gap-3 text-sm sm:grid-cols-2">
                  <div className="rounded-lg border border-border/70 bg-muted/35 px-3 py-2">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Email</p>
                    <p className="mt-1 truncate font-medium">{email || '—'}</p>
                  </div>
                  <div className="rounded-lg border border-border/70 bg-muted/35 px-3 py-2">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">Badges</p>
                    <p className="mt-1 font-medium">{userBadges.length}</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <Card className={cn('xl:col-span-2', embedded && 'border-border/70 bg-card/95 shadow-sm')}>
            <CardHeader>
              <CardTitle>Password</CardTitle>
              <CardDescription>Update your password</CardDescription>
            </CardHeader>
            <CardContent>
              <PasswordSettings />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default function Profile() {
  return (
    <LandingShell showSectionLinks={false}>
      <div className="mx-auto w-full max-w-7xl px-6 py-10 md:py-14">
        <div className="rounded-2xl bg-card p-6 shadow-card md:p-10">
          <ProfilePanel embedded />
        </div>
      </div>
    </LandingShell>
  );
}